import { FileSaverOptions, saveAs } from "file-saver";
import { createWriteStream } from "streamsaver";
import { v4 } from "uuid";
import { getFilenameFromUrl } from "./fileLinks";
import ZIP from "./zip";

export type PipeStreamProgressFunctionPayload = {
  status: "fetching" | "completed" | "canceled",
  fileName: string,
  size: number,
  downloaded: number,
  id: string
  key?: string
};

export type PipeStreamProgressFunction = (progress: PipeStreamProgressFunctionPayload) => void;

export type PipeStreamOptions = {
  fileName?: string,
  size?: number,
  onProgress?: PipeStreamProgressFunction
}

export const downloadBlob = function (data: Blob | string, filename?: string, options?: FileSaverOptions) {
  saveAs(data, filename, options);
};

export const downloadBlobPart = function (data: BlobPart, fileName: string, mimeType: string) {
  saveAs(
    new Blob([ data ], { type: mimeType }),
    fileName,
  );
};

async function pipeStream(writer: WritableStreamDefaultWriter, reader: ReadableStreamDefaultReader, options?: PipeStreamOptions) {
  const id = v4();
  let downloaded = 0;

  const onProgress = (payload: Partial<PipeStreamProgressFunctionPayload>) => {
    options?.onProgress?.({
      fileName: options?.fileName || "",
      status: "fetching",
      downloaded,
      size: options?.size ?? writer.desiredSize ?? 0,
      id,
      ...payload,
    });
  };

  const pump: () => any = async () => {
    try {
      const res = await reader.read();
      if (!res.done) {
        await writer.write(res.value);
        downloaded += res.value.length;
        onProgress({ status: "fetching" });
        return await pump();
      }
      onProgress({ status: "completed" });
      await writer.close();
    } catch (e) {
      await writer.abort("Canceled:" + e);
      await reader.cancel("Canceled:" + e);
      onProgress({ status: "canceled" });
    }
  };

  await pump();
  onProgress({ status: "completed" });
}

export function extractFileSize(res: Response) {
  const contentLength = Number(res.headers.get("content-length"));
  return Number.isNaN(contentLength) ? undefined : (contentLength || undefined);
}

export const downloadURL = async function (url: string, options?: { fileName?: string, onProgress?: PipeStreamProgressFunction }) {
  const fetchResult = await fetch(url, {
    mode: "cors",
  });

  const size = extractFileSize(fetchResult);
  const filename = options?.fileName || getFilenameFromUrl(url);

  const fileStream = createWriteStream(filename, { size });

  await pipeStream(
    fileStream.getWriter(),
    fetchResult.body!.getReader(),
    {
      size,
      fileName: filename,
      onProgress: options?.onProgress,
    },
  );
};

export const downloadURLs = async function (links: (string | { url: string, name: string })[], options?: {
  archiveFileName: string,
  onProgress?: PipeStreamProgressFunction
  totalSize?: number
}) {
  const fileName = options?.archiveFileName || "archive.zip";
  const size = options?.totalSize || (await Promise.all(links.map(async l => {
    try {
      const url = typeof l === "string" ? l : l.url;
      const fetchResult = await fetch(url, {
        method: "HEAD",
        mode: "cors",
      });

      return extractFileSize(fetchResult) || 0;
    } catch (e) {
      return 0;
    }
  }))).reduce((acc, val) => acc + val, 0);

  const fileStream = createWriteStream(fileName, { size: size });

  const pipeStreamOptions: PipeStreamOptions = {
    size: size || 0,
    fileName,
    onProgress: options?.onProgress,
  };

  const files = links.values();
  // @ts-ignore
  const readableZipStream = new ZIP({
    pull(ctrl: any) {
      const it = files.next();
      if (it.done) {
        ctrl.close();
      } else {
        const url = typeof it.value === "string" ? it.value : it.value.url;
        const name = typeof it.value === "string" ? getFilenameFromUrl(url) : it.value.name;

        return new Promise(resolve => {
          fetch(url, { mode: "cors" })
            .then(res => {
              if (!size) {
                pipeStreamOptions.size! += extractFileSize(res) || 0;
              }
              ctrl.enqueue({
                name,
                stream: () => res.body,
              });
            })
            .finally(() => resolve(true));
        });
      }
    },
  });

  await pipeStream(
    fileStream.getWriter(),
    readableZipStream.getReader(),
    pipeStreamOptions,
  );
};

export const fileToBase64 = (file: File) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = () => resolve(reader.result);
  reader.onerror = error => reject(error);
});

